import { useEffect, useState } from "react";
import { Copy, Download, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "../Modal";
import { trackEvent } from "../../lib/analytics";
import { PopButton } from "../PopButton";
import { downloadBlob, renderShareCard, shareCard, shareLink, shareText, type SharePayload } from "../../lib/share";

export function ShareDialog({ payload, onClose }: { payload: SharePayload | null; onClose: () => void }) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string>("");

  useEffect(() => {
    if (!payload) return;
    let objectUrl = "";
    setBlob(null);
    renderShareCard(payload).then((b) => {
      objectUrl = URL.createObjectURL(b);
      setBlob(b);
      setUrl(objectUrl);
    });
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [payload]);

  const onShare = async () => {
    if (!blob || !payload) return;
    try {
      const how = await shareCard(payload, blob);
      trackEvent("share", { kind: how });
      if (how === "downloaded") toast.success("Card downloaded — go show it off!");
    } catch {
      /* user cancelled */
    }
  };

  const onCopy = async () => {
    if (!payload) return;
    await navigator.clipboard?.writeText(shareText(payload)).catch(() => undefined);
    toast.success("Copied to clipboard");
  };

  const onCopyLink = async () => {
    if (!payload) return;
    await navigator.clipboard?.writeText(shareLink(payload)).catch(() => undefined);
    toast.success("Link copied — it shows a DITHER preview in chats");
    trackEvent("share", { kind: "link" });
  };

  return (
    <Modal open={!!payload} onClose={onClose} testId="share-dialog">
      <h3 className="font-display text-2xl font-bold uppercase">Share the verdict</h3>
      <p className="mb-4 text-sm font-medium text-zinc-600">A DITHER card, ready for the group chat.</p>
      <div className="mb-4 overflow-hidden rounded-2xl border-[3px] border-ink bg-pop-yellow" style={{ aspectRatio: "4 / 5" }}>
        {url && blob ? <img src={url} alt="DITHER share card" className="h-full w-full object-cover" data-testid="share-card-preview" />
          : <div className="flex h-full items-center justify-center font-display text-xl font-bold animate-pulse">Drawing your card…</div>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <PopButton tone="teal" size="sm" onClick={onShare} disabled={!blob} data-testid="share-dialog-share-button"><Share2 className="h-4 w-4" />Share</PopButton>
        <PopButton tone="yellow" size="sm" onClick={() => blob && downloadBlob(blob)} disabled={!blob} data-testid="share-dialog-download-button"><Download className="h-4 w-4" />Save</PopButton>
        <PopButton tone="white" size="sm" onClick={onCopy} data-testid="share-dialog-copy-button"><Copy className="h-4 w-4" />Copy</PopButton>
      </div>
      <PopButton tone="purple" size="sm" className="mt-2 w-full" onClick={onCopyLink} data-testid="share-dialog-copy-link-button"><Link2 className="h-4 w-4" />Copy preview link</PopButton>
    </Modal>
  );
}
